import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import '../../styles/InquiryManagement.css';

const InquiryManagement = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [filteredInquiries, setFilteredInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  useEffect(() => {
    fetchInquiries();
  }, []);

  useEffect(() => {
    filterInquiries();
  }, [searchTerm, statusFilter, inquiries]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        navigate('/admin/dashboard');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [navigate]);

  const fetchInquiries = async () => {
    try {
      const response = await api.get('/inquiry/get_inquiries.php');
      if (response.data.success) {
        setInquiries(response.data.data);
        setFilteredInquiries(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterInquiries = () => {
    let filtered = inquiries;

    // Filter by status
    if (statusFilter !== 'All') {
      filtered = filtered.filter(inq => inq.status === statusFilter);
    }

    // Filter by search term (including source)
    if (searchTerm) {
      filtered = filtered.filter(inq =>
        inq.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inq.mobile_no.includes(searchTerm) ||
        inq.area?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inq.source?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredInquiries(filtered);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) {
      return;
    }

    try {
      const response = await api.post('/inquiry/delete_inquiry.php', { id });
      if (response.data.success) {
        alert('Inquiry deleted successfully!');
        fetchInquiries();
      }
    } catch (error) {
      alert('Failed to delete inquiry');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await api.post('/inquiry/update_status.php', { id, status: newStatus });
      if (response.data.success) {
        fetchInquiries();
      }
    } catch (error) {
      alert('Failed to update status');
    }
  };

  // Export filtered inquiries to CSV
  const handleExport = () => {
    if (filteredInquiries.length === 0) {
      alert('No data to export');
      return;
    }

    // Prepare CSV data
    const headers = ['ID', 'Name', 'Mobile', 'Area', 'Date', 'Source', 'Status', 'Plan to Join (days)', 'Reminder Date'];
    const csvData = filteredInquiries.map(inq => [
      inq.id,
      inq.full_name,
      inq.mobile_no,
      inq.area || '',
      formatDate(inq.date),
      inq.source,
      inq.status,
      inq.plan_to_join_days || '',
      formatDate(inq.reminder_date)
    ]);

    // Create CSV content
    let csvContent = headers.join(',') + '\n';
    csvData.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
    });

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `inquiries_${statusFilter.toLowerCase()}_${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN');
  };

  return (
    <div className="inquiry-management-page">
      <div className="inquiry-management-container">
        {/* Header */}
        <div className="inquiry-header">
          <div className="header-left">
            <button className="back-button" onClick={() => navigate('/admin/dashboard')} title="Back (ESC)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h2>Inquiry Management</h2>
              <p>View and manage all inquiries</p>
            </div>
          </div>
          <button className="btn-add-inquiry" onClick={() => navigate('/admin/inquiries/add')}>
            ➕ Add Inquiry
          </button>
        </div>

        {/* Filters */}
        <div className="inquiry-filters">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name, mobile, area, or source..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="status-filters">
            {['All', 'Open', 'Contacted', 'Interested', 'Not Interested', 'Converted', 'Closed'].map(status => (
              <button
                key={status}
                className={`filter-btn ${statusFilter === status ? 'active' : ''}`}
                onClick={() => setStatusFilter(status)}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Inquiries Table */}
        <div className="inquiry-table-container">
          {loading ? (
            <div className="loading">Loading inquiries...</div>
          ) : filteredInquiries.length === 0 ? (
            <div className="no-data">No inquiries found</div>
          ) : (
            <table className="inquiry-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Area</th>
                  <th>Date</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInquiries.map((inquiry) => (
                  <tr key={inquiry.id}>
                    <td>{inquiry.id}</td>
                    <td>{inquiry.full_name}</td>
                    <td>{inquiry.mobile_no}</td>
                    <td>{inquiry.area || '-'}</td>
                    <td>{formatDate(inquiry.date)}</td>
                    <td>{inquiry.source}</td>
                    <td>
                      <select
                        className={`status-select status-${inquiry.status.toLowerCase().replace(' ', '-')}`}
                        value={inquiry.status}
                        onChange={(e) => handleStatusChange(inquiry.id, e.target.value)}
                      >
                        <option value="Open">Open</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Interested">Interested</option>
                        <option value="Not Interested">Not Interested</option>
                        <option value="Converted">Converted</option>
                        <option value="Closed">Closed</option>
                      </select>
                    </td>
                    <td className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => navigate(`/admin/inquiries/edit/${inquiry.id}`)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(inquiry.id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Summary with Export */}
        <div className="inquiry-summary">
          <div className="summary-left">
            <p>Total Inquiries: <strong>{filteredInquiries.length}</strong> of {inquiries.length}</p>
            {statusFilter !== 'All' && (
              <span className="filter-info">Filtered by: {statusFilter}</span>
            )}
          </div>
          <button 
            className="btn-export"
            onClick={handleExport}
            disabled={filteredInquiries.length === 0}
          >
            📥 Export Records
          </button>
        </div>
      </div>
    </div>
  );
};

export default InquiryManagement;
